/**
 * Context do Carrinho
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { carrinhoService } from '@services';
import { CarrinhoState, TipoCarrinho, CarrinhoResponse, Produto, SessionKeys } from '@types';
import { CarrinhoUtil, SessionUtil } from '@utils';

interface CarrinhoContextData {
  carrinho: CarrinhoState;
  isLoading: boolean;
  error: string | null;
  // Ações
  addItem: (produto: Produto, quantidade: number, precoEditado?: number) => void;
  removeItem: (codigoProduto: string) => void;
  updateQuantity: (codigoProduto: string, quantidade: number) => void;
  updatePrice: (codigoProduto: string, novoPreco: number) => void;
  clearCarrinho: () => void;
  enviarCarrinho: () => Promise<CarrinhoResponse>;
  // Utilitários
  hasItem: (codigoProduto: string) => boolean;
  getItemQuantity: (codigoProduto: string) => number;
  getQuantidadeEstoqueLoja: (codigoProduto: string) => number;
  isUsandoEstoqueLoja: (codigoProduto: string) => boolean;
  isEmpty: boolean;
  totalItems: number;
  clearError: () => void;
}

const CarrinhoContext = createContext<CarrinhoContextData | undefined>(undefined);

interface CarrinhoProviderProps {
  children: React.ReactNode;
  tipo: TipoCarrinho;
}

export const CarrinhoProvider: React.FC<CarrinhoProviderProps> = ({ children, tipo }) => {
  const [carrinho, setCarrinho] = useState<CarrinhoState>(() => {
    const vendedor = SessionUtil.getVendedor();
    const pedidoAtivo = Number(vendedor?.indPedido ?? 0) !== 0;

    // Se pedido está ativo, não carrega carrinho de previsão do localStorage
    if (tipo === 'previsao' && pedidoAtivo) {
      console.log('[CARRINHO] Pedido ativo — carrinho de previsão não será carregado do storage.');
      return CarrinhoUtil.createEmptyCarrinho('previsao');
    }

    // Se está iniciando um carrinho de PEDIDO, limpa o carrinho de PREVISÃO se houver
    if (tipo === 'pedido') {
      const savedTipo = SessionUtil.get<TipoCarrinho>(SessionKeys.CARRINHO_TIPO);
      if (savedTipo === 'previsao') {
        const previsaoExistente = carrinhoService.loadCarrinho('previsao');
        if (!CarrinhoUtil.isEmpty(previsaoExistente)) {
          console.log('Iniciando pedido: limpando carrinho de previsão existente');
          CarrinhoUtil.clearCarrinho('previsao');
        }
      }
    }

    return carrinhoService.loadCarrinho(tipo);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // Salva carrinho com debounce para melhorar performance
  useEffect(() => {
    // Cancela timeout anterior se existir
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Agenda nova gravação com debounce de 100ms
    saveTimeoutRef.current = setTimeout(() => {
      carrinhoService.saveCarrinho(carrinho);
    }, 100);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [carrinho]);

  const addItem = useCallback(
    (produto: Produto, quantidade: number, precoEditado?: number) => {
      try {
        setCarrinho((currentCarrinho) => {
          const newCarrinho = CarrinhoUtil.addItem(currentCarrinho, produto, quantidade, precoEditado);
          return newCarrinho;
        });
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Erro ao adicionar item');
      }
    },
    []
  );

  const removeItem = useCallback(
    (codigoProduto: string) => {
      try {
        setCarrinho((currentCarrinho) => {
          const newCarrinho = CarrinhoUtil.removeItem(currentCarrinho, codigoProduto);
          return newCarrinho;
        });
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Erro ao remover item');
      }
    },
    []
  );

  const updateQuantity = useCallback(
    (codigoProduto: string, quantidade: number) => {
      setCarrinho((currentCarrinho) => {
        if (!currentCarrinho.items.has(codigoProduto)) {
          setError('Item não encontrado no carrinho e foi removido.');
          return CarrinhoUtil.removeItem(currentCarrinho, codigoProduto);
        }
        try {
          const newCarrinho = CarrinhoUtil.updateQuantity(currentCarrinho, codigoProduto, quantidade);
          setError(null);
          return newCarrinho;
        } catch (err: any) {
          setError(err.message || 'Erro ao atualizar quantidade');
          return currentCarrinho;
        }
      });
    },
    [tipo]
  );

  const updatePrice = useCallback(
    (codigoProduto: string, novoPreco: number) => {
      try {
        setCarrinho((currentCarrinho) => {
          const newCarrinho = CarrinhoUtil.updatePrice(currentCarrinho, codigoProduto, novoPreco);
          return newCarrinho;
        });
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Erro ao atualizar preço');
      }
    },
    []
  );

  const clearCarrinho = useCallback(() => {
    const newCarrinho = CarrinhoUtil.clearCarrinho(tipo);
    setCarrinho(newCarrinho);
    setError(null);
  }, [tipo]);

  const enviarCarrinho = useCallback(async (): Promise<CarrinhoResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await carrinhoService.enviar(carrinho);

      if (response.retorno !== 'ok') {
        setError(response.mensagem);
      }

      return response;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao enviar carrinho';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [carrinho]);

  const hasItem = useCallback(
    (codigoProduto: string) => {
      return CarrinhoUtil.hasItem(carrinho, codigoProduto);
    },
    [carrinho]
  );

  const getItemQuantity = useCallback(
    (codigoProduto: string) => {
      return CarrinhoUtil.getItemQuantity(carrinho, codigoProduto);
    },
    [carrinho]
  );

  const getQuantidadeEstoqueLoja = useCallback(
    (codigoProduto: string) => {
      return CarrinhoUtil.getQuantidadeEstoqueLoja(carrinho, codigoProduto);
    },
    [carrinho]
  );

  const isUsandoEstoqueLoja = useCallback(
    (codigoProduto: string) => {
      return CarrinhoUtil.isUsandoEstoqueLoja(carrinho, codigoProduto);
    },
    [carrinho]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isEmpty = CarrinhoUtil.isEmpty(carrinho);
  const totalItems = CarrinhoUtil.getTotalItems(carrinho);

  return (
    <CarrinhoContext.Provider
      value={{
        carrinho,
        isLoading,
        error,
        addItem,
        removeItem,
        updateQuantity,
        updatePrice,
        clearCarrinho,
        enviarCarrinho,
        hasItem,
        getItemQuantity,
        getQuantidadeEstoqueLoja,
        isUsandoEstoqueLoja,
        isEmpty,
        totalItems,
        clearError,
      }}
    >
      {children}
    </CarrinhoContext.Provider>
  );
};

export const useCarrinhoContext = () => {
  const context = useContext(CarrinhoContext);

  if (!context) {
    throw new Error('useCarrinhoContext must be used within CarrinhoProvider');
  }

  return context;
};